import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Modal,
  Backdrop,
  Fade,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  TextField,
  Autocomplete,
  Badge,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import FilterBar from "../../FilterSearch/FilterBarTables";
import { ThemeContext } from "../../../../../context/ThemeContextProvider";
import { fetchAllTables } from "../../../../../services/pos/Tables";
import { fetchPendingReservationForTable } from "../../../../../services/pos/reservation/reservationApi";
import TableModalContent from "../../MakeOrderResponsive/TabletMakeOrder/TableTablet/TabletTableModalContent";
import CreateOrderForm from "../../MakeOrderResponsive/TabletMakeOrder/TableTablet/TabletOrderDetails/TabletOpenorder/CreateOrderForm";
import { changeOrderStatus } from "../../../../../services/OrderStatus/OrderStatusApi";
import { useSettings } from "../../../../../context/SettingsContext";
import { usePermissions } from "../../../../../context/PermissionsAndFeatures/UserPermissionsContext";
import OrderItemsDialog from "./OrderItemsDialog";

const statusColors = {
  مفتوح: "#4CAF50",
  متاح: "#03A9F4",
  محجوز: "#F44336",
  "اوردر خارجي": "#fff",
};

const TabletTablesSection = () => {
  const { settings } = useSettings();
  const { permissions } = usePermissions();
  const canOpenTables = permissions["orders_tables_open_table"] === 1;

  const [showNoPermission, setShowNoPermission] = useState(false);
  const { mode } = useContext(ThemeContext);
  const theme = useTheme();

  // ✅ كشف دقيق لجميع أحجام التابلت
  const isSmallTablet = useMediaQuery("(min-width: 600px) and (max-width: 768px)");
  const isMediumTablet = useMediaQuery("(min-width: 769px) and (max-width: 900px)");
  const isLargeTablet = useMediaQuery("(min-width: 901px) and (max-width: 1024px)");
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 1024px)");
  
  // ✅ كشف الوضع الأفقي/العمودي
  const isPortrait = useMediaQuery("(orientation: portrait)");
  const isLandscape = useMediaQuery("(orientation: landscape)");

  // ✅ المقاس المحدد 600-767px × 743px
  const isSpecificSize = useMediaQuery("(min-width: 600px) and (max-width: 767px) and (min-height: 700px) and (max-height: 780px)");

  // ✅ حساب أحجام العناصر
  const getBoxPadding = () => {
    if (isSpecificSize) return isPortrait ? 1 : 1.5;
    if (isSmallTablet) return isPortrait ? 1.5 : 2;
    if (isMediumTablet) return isPortrait ? 2 : 2.5;
    return isPortrait ? 2 : 3;
  };

  const getCardPadding = () => {
    if (isSpecificSize) return 1.2;
    if (isSmallTablet) return 1.5;
    if (isMediumTablet) return 1.8;
    return 2;
  };

  const getCardMinHeight = () => {
    if (isSpecificSize) return isPortrait ? "130px" : "140px";
    if (isSmallTablet) return isPortrait ? "140px" : "150px";
    if (isMediumTablet) return isPortrait ? "160px" : "170px";
    return isPortrait ? "170px" : "180px";
  };

  const getFontSizes = () => {
    if (isSpecificSize) return { title: "13px", body: "11px", small: "10px" };
    if (isSmallTablet) return { title: "14px", body: "12px", small: "11px" };
    if (isMediumTablet) return { title: "15px", body: "13px", small: "12px" };
    return { title: "16px", body: "14px", small: "13px" };
  };

  const getBadgeSizes = () => {
    if (isSpecificSize) return { px: 0.8, py: 0.3, fontSize: "11px" };
    if (isSmallTablet) return { px: 1, py: 0.4, fontSize: "12px" };
    if (isMediumTablet) return { px: 1.2, py: 0.5, fontSize: "13px" };
    return { px: 1.5, py: 0.5, fontSize: "14px" };
  };

  const getButtonSize = () => {
    if (isSpecificSize) return "small";
    return isSmallTablet ? "small" : "medium";
  };

  const getGridColumns = () => {
    if (isSpecificSize) {
      return isPortrait ? { xs: 12, sm: 6 } : { xs: 6, sm: 4 };
    }
    if (isSmallTablet) {
      return isPortrait ? { xs: 12, sm: 6 } : { xs: 6, sm: 4 };
    }
    if (isMediumTablet) {
      return isPortrait ? { xs: 6, sm: 4 } : { xs: 4, sm: 2.4 };
    }
    // CHANGED: تغيير lg من 3 إلى 2.4 ليصبح 5 أعمدة (12/2.4=5)
    return { xs: 12, sm: 6, md: 3, lg: 2.4 };
  };

  const fontSize = getFontSizes();
  const badgeSize = getBadgeSizes();
  const gridCols = getGridColumns();

  const [tables, setTables] = useState([]);
  const [filteredTables, setFilteredTables] = useState([]);
  const [activeTopTab, setActiveTopTab] = useState("all");
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    captain: "",
  });
  // NEW: حالة فلتر اختيار/كتابة رقم الطاولة
  const [tableFilter, setTableFilter] = useState("");
  // NEW: حالة فلتر الكابتن (ديناميكي من بيانات الطاولات)
  const [captainFilter, setCaptainFilter] = useState("");
  const [selectedTable, setSelectedTable] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectSubOrderModal, setSelectSubOrderModal] = useState(false);
  const [subOrders, setSubOrders] = useState([]);
  const [pendingTable, setPendingTable] = useState(null);
  const [pendingReservations, setPendingReservations] = useState({});
  const [showConfirmSuccess, setShowConfirmSuccess] = useState(false);
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [reservationDialogLoading, setReservationDialogLoading] = useState(false);
  const [reservationDialogData, setReservationDialogData] = useState([]);
  const [reservationDialogTable, setReservationDialogTable] = useState(null);

  // NEW: حالة Dialog عرض أصناف الأوردر
  const [orderItemsDialogOpen, setOrderItemsDialogOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const rootId = localStorage.getItem("branch_id");

  const enrichTables = (data, settings) => {
    // استخدم reservation_time من settings (بالدقائق). إذا لم يوجد، ارجع إلى reservation_alert_before أو 60 كقيمة افتراضية.
    const alertBeforeMinutes = Number(
      settings?.reservation_time ?? settings?.reservation_alert_before ?? 60
    );

    return data.map((table) => {
      const order = table.order || {};
      let status = table.status || (table.available ? "متاح" : "مفتوح");

      let nearestReservation = null;
      if (Array.isArray(table.reservations) && table.reservations.length > 0) {
        const now = new Date();
        nearestReservation = table.reservations
          .map((r) => {
            const dt = new Date(`${r.reservation_date}T${r.reservation_time}`);
            return { ...r, dt, diffMinutes: Math.round((dt - now) / 60000) };
          })
          .filter((r) => r.diffMinutes >= 0)
          .sort((a, b) => a.diffMinutes - b.diffMinutes)[0];
      }

      if (
        nearestReservation &&
        nearestReservation.diffMinutes >= 0 &&
        nearestReservation.diffMinutes <= alertBeforeMinutes
      ) {
        status = "محجوز";
      }

      // استخراج حالة الأوردر ووقت الفتح مع fallbacks (بما في ذلك احتمالية وجود nested order)
      const orderStatus =
        order.status || order.order?.status || table.order?.status || "";
      const openTime =
        order.open_time ||
        order.openTime ||
        order.order?.open_time ||
        table.order?.open_time ||
        "";

      // تحديد وجود أوردرات فرعية بناءً على الحقول المتاحة داخل order
      const hasSubOrders =
        Boolean(order.has_sub_orders) ||
        Boolean(order.hasSubOrders) ||
        (Array.isArray(order.sub_orders) && order.sub_orders.length > 0) ||
        Boolean(table.hasSubOrders);

      // normalize sub orders array so it's easy للاستخدام لاحقًا
      const subOrders = Array.isArray(order.sub_orders)
        ? order.sub_orders.map((s) => ({
            id: s.id,
            sub_table_name: s.sub_table_name || s.name || `فرعي ${s.id}`,
            open_time: s.open_time || s.openTime || "",
            customer: s.customer || "",
            total: s.total || 0,
            pending_items_count: s.pending_items_count ?? 0,
            // keep raw for any other usage
            ...s,
          }))
        : [];

      return {
        ...table,
        status,
        nearestReservation,
        customer: order.customer || "",
        captain: order.captain_name || "",
        total: order.total || 0,
        orderId: order.id || null,
        // الحقول الجديدة للعرض
        orderStatus,
        openTime,
        // حقل يوضح وجود أوردرات فرعية
        hasSubOrders,
        subOrders,
        // NEW: عدد الأصناف في الأوردر
        orderItemsCount: order.order_items_count ?? 0,
      };
    });
  };

  const loadTables = async () => {
    try {
      setLoading(true);
      const data = await fetchAllTables();
      const enriched = enrichTables(data, settings); // ✅ مرر settings هنا
      setTables(enriched);
      // إذا التاب العلوي مفعل، خزن النتائج المناسبة، وإلا اعرض الكل
      if (activeTopTab === "under_review") {
        setFilteredTables(enriched.filter((t) => t.orderStatus === "under_review"));
      } else {
        setFilteredTables(enriched);
      }
    } catch (error) {
      console.error("❌ فشل في تحميل الطاولات:", error.message);
    } finally {
      setLoading(false);
    }
  };

  // جلب بيانات الحجز للطاولات المحجوزة فقط
  const fetchReservationsForReservedTables = async (tablesList) => {
    const reservedTables = tablesList.filter((t) => t.status === "محجوز");
    const reservationsObj = {};
    await Promise.all(
      reservedTables.map(async (table) => {
        try {
          const reservations = await fetchPendingReservationForTable(table.id);
          if (reservations && reservations.length > 0) {
            // احصل على أقرب حجز (حسب الوقت)
            const now = new Date();
            const sorted = reservations
              .map((r) => ({
                ...r,
                diffMinutes: Math.round(
                  (new Date(r.reservation_time) - now) / 60000
                ),
              }))
              .filter((r) => r.diffMinutes >= 0)
              .sort((a, b) => a.diffMinutes - b.diffMinutes);
            if (sorted.length > 0) {
              reservationsObj[table.id] = sorted[0];
            }
          }
        } catch (e) {
          // تجاهل الخطأ
        }
      })
    );
    setPendingReservations(reservationsObj);
  };

  // عند تحميل الطاولات أو تحديثها، جلب بيانات الحجز للطاولات المحجوزة (تحديث البيانات كل 1 دقيقة)
  useEffect(() => {
    loadTables();
    // تحديث تلقائي كل 9 ثواني
    const interval = setInterval(() => {
      loadTables();
    }, 9000000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem("current_component", "tables");
  }, []);

  // useEffect(() => {
  //   window.refreshTables = loadTables;
  //   return () => {
  //     delete window.refreshTables;
  //   };
  // }, []);

  window.updateTableData = (tableId) => {
    setTables((prevTables) =>
      prevTables.map((t) =>
        t.id === tableId
          ? {
              ...t,
              status: "مفتوح",
              available: 0,
            }
          : t
      )
    );
    setFilteredTables((prevTables) =>
      prevTables.map((t) =>
        t.id === tableId
          ? {
              ...t,
              status: "مفتوح",
              available: 0,
            }
          : t
      )
    );
  };

  // عد الطاولات التي في حالة under_review
  const underReviewCount = tables.filter((t) => t.orderStatus === "under_review").length;

  // NEW: قائمة أرقام/أسماء الطاولات الفريدة
  const uniqueTableNumbers = [...new Set(tables.map((t) => t.name?.toString()).filter(Boolean))].sort((a, b) => {
    const na = parseInt(a), nb = parseInt(b);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return a.localeCompare(b);
  });

  // NEW: قائمة الكباتن الفريدة من بيانات الطاولات
  const uniqueCaptains = [...new Set(tables.map((t) => (t.captain || "").toString()).filter(Boolean))].sort((a,b)=> a.localeCompare(b));

  // عندما يتغير التاب العلوي طبق الفلتر على بيانات الطاولات الحالية
  useEffect(() => {
    // تطبيق سلسلة من الفلاتر: تاب أعلى -> فلتر الطاولة -> فلتر الكابتن
    let filtered = tables.slice();

    if (activeTopTab === "under_review") {
      filtered = filtered.filter((t) => t.orderStatus === "under_review");
    }

    if (tableFilter && tableFilter.toString().trim() !== "") {
      const tf = tableFilter.toString().toLowerCase();
      filtered = filtered.filter(
        (t) => (t.name || "").toString().toLowerCase().includes(tf)
      );
    }

    if (captainFilter && captainFilter.toString().trim() !== "") {
      const cf = captainFilter.toString().toLowerCase();
      filtered = filtered.filter((t) => (t.captain || "").toString().toLowerCase().includes(cf));
    }

    setFilteredTables(filtered);
  }, [activeTopTab, tables, tableFilter, captainFilter]);

  const handleFilterChange = (newFilters) => {
    const filtered = tables.filter((table) => {
      return (
        (newFilters.search === "" ||
          table.customer.includes(newFilters.search)) &&
        (newFilters.status === "" || table.status === newFilters.status) &&
        (newFilters.captain === "" || table.captain === newFilters.captain)
      );
    });
    setFilteredTables(filtered);
  };

  const handleTableClick = async (table) => {
    const updatedTable = tables.find((t) => t.id === table.id);

    // تحقق من صلاحية فتح الطاولة
    if (!canOpenTables) {
      // عرض رسالة للمستخدم ومنع فتح المودال
      setShowNoPermission(true);
      return;
    }

    if (!updatedTable) return;

    // إذا توجد أوردرات فرعية، عرض مودال اختيار الأوردر أولاً
    if (updatedTable.hasSubOrders && updatedTable.order) {
      setPendingTable(updatedTable);

      // الأوردر الأساسي (main) من بيانات order نفسها
      const mainOrder = {
        id: updatedTable.orderId || updatedTable.order.id,
        sub_table_name:
          updatedTable.order.sub_table_name ||
          updatedTable.name ||
          `أساسي (${updatedTable.name || updatedTable.orderId || ""})`,
        open_time:
          updatedTable.openTime ||
          updatedTable.order.open_time ||
          updatedTable.order.openTime ||
          "",
        customer:
          updatedTable.order.customer ||
          updatedTable.customer ||
          updatedTable.order.customer_name ||
          "",
        total: updatedTable.order.total || updatedTable.total || 0,
        pending_items_count:
          updatedTable.order.pending_items_count ?? updatedTable.order.pending_items_count ?? 0,
        isMain: true,
      };

      const subOrdersList = [
        mainOrder,
        ...((Array.isArray(updatedTable.subOrders) ? updatedTable.subOrders : []).map((s) => ({
          id: s.id,
          sub_table_name: s.sub_table_name || `فرعي ${s.id}`,
          open_time: s.open_time || "",
          customer: s.customer || "",
          total: s.total || 0,
          pending_items_count: s.pending_items_count ?? 0,
          isMain: false,
        }))),
      ];

      setSubOrders(subOrdersList);
      setSelectSubOrderModal(true);
      return;
    }

    setSelectedTable(updatedTable);

    if (updatedTable.available === 1) {
      setModalType("createOrder");
      setIsModalOpen(true);
    } else {
      setOrderId(updatedTable.orderId);
      setModalType("addItem");
      setIsModalOpen(true);
    }

    // تحديث البيانات بعد الإجراء
  };

  const handleSelectSubOrder = (orderObj) => {
    setSelectSubOrderModal(false);
    if (!pendingTable) return;
    setSelectedTable(pendingTable);
    setOrderId(orderObj.id);
    setModalType("addItem");
    setIsModalOpen(true);
    setPendingTable(null);
  };

  const refreshAndReopen = async () => {
    // لو عندك selectedTable بالفعل، بس عدل حالته داخليًا بدل ما تعمل fetchAllTables
    if (selectedTable) {
      setSelectedTable((prev) => ({
        ...prev,
        orderId: orderId, // أو أي قيم محدثة عندك
        status: "مفتوح",
      }));

      setModalType("addItem");
      setIsModalOpen(true);
    }
  };

  const handleOrderDeleteSuccess = () => {
    if (!selectedTable) return;

    const updatedTable = {
      ...selectedTable,
      status: "متاح",
      available: 1,
      orderId: null,
      customer: "",
      captain: "",
      total: "0",
      openDateTime: "",
      // مسح الحقول الجديدة
      openTime: "",
      orderStatus: "",
    };

    const newTables = tables.map((t) =>
      t.id === updatedTable.id ? updatedTable : t
    );

    setTables(newTables);
    setFilteredTables(newTables);
    setSelectedTable(updatedTable);
    setIsModalOpen(false);
    // تحديث البيانات بعد الإجراء
  };

  const handleShowReservations = async (table) => {
    setReservationDialogOpen(true);
    setReservationDialogTable(table);
    setReservationDialogLoading(true);
    try {
      const data = await fetchPendingReservationForTable(table.id);
      setReservationDialogData(Array.isArray(data) ? data : []);
    } catch {
      setReservationDialogData([]);
    }
    setReservationDialogLoading(false);
  };

  const handleConfirmExternalOrder = async (table) => {
    try {
      if (!table.orderId) {
        alert("لا يوجد رقم أوردر للطاولة!");
        return;
      }
      await changeOrderStatus(table.orderId, "pending");
      setShowConfirmSuccess(true); // إظهار رسالة النجاح
    } catch (err) {
      alert("حدث خطأ أثناء تأكيد الأوردر الخارجي");
      console.error(err);
    }
  };

  const formatTime12 = (timeStr) => {
    if (!timeStr) return "-";
    const [h, m] = timeStr.split(":");
    let hour = Number(h);
    const ampm = hour >= 12 ? "م" : "ص";
    hour = hour % 12 || 12;
    return `${hour}:${m} ${ampm}`;
  };

  // NEW: دالة فتح Dialog عرض الأصناف
  const handleOpenOrderItems = (orderId) => {
    setSelectedOrderId(orderId);
    setOrderItemsDialogOpen(true);
  };
  
  // NEW: دالة إغلاق Dialog عرض الأصناف
  const handleCloseOrderItems = () => {
    setOrderItemsDialogOpen(false);
    setSelectedOrderId(null);
  };

  return (
    <Box 
      sx={{ 
        padding: getBoxPadding(),
        // ✅ تحسين الأداء
        willChange: "transform",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {/* تاب علوي للطلبات قيد المراجعة */}
      <Box 
        sx={{ 
          display: "flex", 
          gap: isSpecificSize ? 0.8 : 1, 
          mb: isSpecificSize ? 1.5 : 2,
          flexWrap: isPortrait && isSmallTablet ? "wrap" : "nowrap",
        }}
      >
        <Button
          variant={activeTopTab === "all" ? "contained" : "outlined"}
          onClick={() => setActiveTopTab("all")}
          size={getButtonSize()}
          sx={{
            fontSize: fontSize.body,
            minHeight: 40,
            flex: isPortrait && isSmallTablet ? "1 1 auto" : "0 0 auto",
          }}
        >
          كل الطاولات
        </Button>
        <Button
          variant={activeTopTab === "under_review" ? "contained" : "outlined"}
          color="warning"
          onClick={() => setActiveTopTab("under_review")}
          size={getButtonSize()}
          sx={{
            fontSize: fontSize.body,
            minHeight: 40,
            flex: isPortrait && isSmallTablet ? "1 1 auto" : "0 0 auto",
          }}
        >
          طلب خارجي ({underReviewCount})
        </Button>
      </Box>

      {/* NEW: شريط فلتر الطاولة والكابتن */}
      <Box 
        sx={{ 
          display: "flex", 
          gap: isSpecificSize ? 1 : 1.5, 
          mb: isSpecificSize ? 1.5 : 2,
          flexWrap: "wrap",
        }}
      >
        {/* فلتر رقم الطاولة */}
        <Autocomplete
          freeSolo
          options={uniqueTableNumbers}
          value={tableFilter}
          onChange={(e, newVal) => setTableFilter(newVal || "")}
          onInputChange={(e, newInput) => setTableFilter(newInput)}
          sx={{ 
            minWidth: isSpecificSize ? 180 : 220,
            flex: 1,
          }}
          renderInput={(params) => (
            <TextField 
              {...params} 
              size="small" 
              placeholder="اختر أو اكتب رقم الطاولة"
              sx={{ fontSize: fontSize.body }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              <Typography sx={{ fontSize: fontSize.body }}>
                طاولة {option}
              </Typography>
            </li>
          )}
          noOptionsText="لا توجد طاولات مطابقة"
        />

        {/* فلتر الكابتن */}
        <Autocomplete
          freeSolo
          options={uniqueCaptains}
          value={captainFilter}
          onChange={(e, newVal) => setCaptainFilter(newVal || "")}
          onInputChange={(e, newInput) => setCaptainFilter(newInput)}
          sx={{ 
            minWidth: isSpecificSize ? 180 : 220,
            flex: 1,
          }}
          renderInput={(params) => (
            <TextField 
              {...params} 
              size="small" 
              placeholder="اختر أو اكتب اسم الكابتن"
              sx={{ fontSize: fontSize.body }}
            />
          )}
          noOptionsText="لا يوجد كباتن مطابقين"
        />

        {/* زر إعادة تعيين الفلاتر */}
        {(tableFilter || captainFilter) && (
          <Button
            variant="outlined"
            color="secondary"
            size={getButtonSize()}
            onClick={() => {
              setTableFilter("");
              setCaptainFilter("");
            }}
            sx={{
              fontSize: fontSize.body,
              minHeight: 40,
              px: isSpecificSize ? 2 : 3,
            }}
          >
            إعادة تعيين
          </Button>
        )}
      </Box>

      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
        >
          <CircularProgress 
            color="secondary" 
            size={isSpecificSize ? 60 : isSmallTablet ? 70 : 80} 
            thickness={5} 
          />
        </Box>
      )}

      <FilterBar
        filters={filters}
        setFilters={setFilters}
        onFilterChange={handleFilterChange}
      />

      <Grid 
        container 
        spacing={isSpecificSize ? 1.5 : isSmallTablet ? 2 : 2}
        sx={{ mt: 0 }}
      >
        {filteredTables.map((table) => (
          <Grid
            item
            {...gridCols}
            key={table.id}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
              <Paper
                sx={{
                  padding: getCardPadding(),
                  bgcolor: mode === "dark" ? "#2C2A3A" : "#fff",
                  color: mode === "dark" ? "#fff" : "#000",
                  borderRadius: isSpecificSize ? "14px" : isSmallTablet ? "16px" : "18px",
                  boxShadow:
                    mode === "dark"
                      ? "0px 4px 12px rgba(0,0,0,0.3)"
                      : "0px 2px 8px rgba(0,0,0,0.1)",
                  position: "relative",
                  cursor: "pointer",
                  minHeight: getCardMinHeight(),
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: "100%",
                  zIndex: 2,
                  overflow: "hidden",
                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  // ✅ تحسين touch targets
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: mode === "dark"
                      ? "0px 6px 16px rgba(0,0,0,0.4)"
                      : "0px 4px 12px rgba(0,0,0,0.15)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
                onClick={async () => {
                  await handleTableClick(table);
                }}
              >
                {/* Table Name Badge */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bgcolor: "#F8BBD0",
                    color: "#fff",
                    borderRadius: isSpecificSize ? "6px 6px 0 6px" : "8px 8px 0 8px",
                    px: badgeSize.px,
                    py: badgeSize.py,
                    fontWeight: "bold",
                    fontSize: fontSize.body,
                    minWidth: isSpecificSize ? "35px" : "40px",
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    zIndex: 3,
                  }}
                >
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    {`طاولة ${table.name}`}
                    {table.orderStatus === "under_review" && (
                      <Box
                        sx={{
                          background: "linear-gradient(90deg, #ff6b6b 0%, #ff8a65 100%)",
                          color: "#ffffff",
                          borderRadius: isSpecificSize ? "8px" : "10px",
                          px: isSpecificSize ? 0.8 : 1.2,
                          py: isSpecificSize ? "2px" : "4px",
                          fontWeight: 900,
                          fontSize: fontSize.small,
                          boxShadow: "0 6px 18px rgba(255,107,107,0.32), 0 0 12px rgba(255,138,101,0.22)",
                          border: "1px solid rgba(255,255,255,0.22)",
                          animation: "pulse 1.6s infinite",
                        }}
                      >
                        طلب خارجي
                      </Box>
                    )}
                  </Box>

                  {table.hasSubOrders && (
                    <span
                      style={{
                        marginRight: isSpecificSize ? 4 : 6,
                        background: "#fff",
                        color: "#d32f2f",
                        borderRadius: isSpecificSize ? "4px" : "6px",
                        padding: isSpecificSize ? "1px 5px" : "2px 7px",
                        fontSize: fontSize.small,
                        fontWeight: 700,
                        boxShadow: "0 2px 8px #0002",
                        display: "inline-block",
                        border: "1.5px solid #d32f2f",
                      }}
                      title="هذه الطاولة تحتوي على أوردرات فرعية (مقرره)"
                    >
                      مقرره
                    </span>
                  )}
                </Box>

                {/* Status Badge */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "0px",
                    left: 0,
                    bgcolor: statusColors[table.status] || "#999",
                    color: table.status === "اوردر خارجي" ? "#232334" : "#fff",
                    borderRadius: isSpecificSize ? "6px 6px 6px 0" : "8px 8px 8px 0",
                    px: isSpecificSize ? 1.5 : 2,
                    py: badgeSize.py,
                    fontWeight: "bold",
                    fontSize: fontSize.body,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: isSpecificSize ? "35px" : "40px",
                    minWidth: isSpecificSize ? "90px" : "100px",
                  }}
                >
                  {table.status}
                  {table.status === "محجوز" && (
                    <LockIcon sx={{ fontSize: fontSize.body, ml: 0.5 }} />
                  )}
                </Box>

                {/* NEW: أيقونة عدد الأصناف - تظهر في منتصف أعلى البطاقة */}
                {table.status !== "متاح" && table.orderItemsCount > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: isSpecificSize ? 6 : 8,
                      left: "50%",
                      transform: "translateX(-50%)",
                      zIndex: 10,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenOrderItems(table.orderId);
                    }}
                  >
                    <Badge
                      badgeContent={table.orderItemsCount}
                      color="error"
                      max={99}
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: isSpecificSize ? "9px" : "10px",
                          fontWeight: 700,
                          minWidth: isSpecificSize ? "18px" : "20px",
                          height: isSpecificSize ? "18px" : "20px",
                          padding: "0 4px",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: mode === "dark" ? "#1a1a2e" : "#fff",
                          borderRadius: "50%",
                          width: isSpecificSize ? 32 : 36,
                          height: isSpecificSize ? 32 : 36,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          border: `2px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
                          transition: "all 0.2s",
                          "&:hover": {
                            transform: "scale(1.1)",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                          },
                          "&:active": {
                            transform: "scale(0.95)",
                          },
                        }}
                      >
                        <RestaurantMenuIcon
                          sx={{
                            fontSize: isSpecificSize ? 16 : 18,
                            color: mode === "dark" ? "#fff" : "#666",
                          }}
                        />
                      </Box>
                    </Badge>
                  </Box>
                )}

                {/* External Order Alert */}
                {table.status === "اوردر خارجي" && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "#fff",
                      color: "#d32f2f",
                      borderRadius: isSpecificSize ? "10px" : "12px",
                      px: isSpecificSize ? 2 : 3,
                      py: isSpecificSize ? 1 : 1.5,
                      fontWeight: "bold",
                      fontSize: fontSize.body,
                      boxShadow: "0 2px 12px #d32f2f22",
                      border: "2px solid #d32f2f",
                      textAlign: "center",
                      zIndex: 20,
                      animation: "blinker 1.2s linear infinite",
                    }}
                  >
                    تنبيه: يوجد أوردر خارجي قيد المراجعة
                  </Box>
                )}

                {/* Reservation Alert */}
                {table.status === "محجوز" && pendingReservations[table.id] && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: isSpecificSize ? 38 : 45,
                      left: isSpecificSize ? 15 : 20,
                      bgcolor: "#ff9800",
                      color: "#fff",
                      borderRadius: isSpecificSize ? "6px" : "8px",
                      px: isSpecificSize ? 1.5 : 2,
                      py: badgeSize.py,
                      fontWeight: "bold",
                      fontSize: fontSize.small,
                      zIndex: 5,
                      boxShadow: "0 2px 8px #ff980088",
                      animation: "blinker 1.2s linear infinite",
                    }}
                  >
                    {(() => {
                      const diff = pendingReservations[table.id].diffMinutes;
                      if (diff < 60) {
                        return `الحجز بعد ${diff} دقيقة`;
                      } else {
                        const hours = Math.floor(diff / 60);
                        const mins = diff % 60;
                        return `الحجز بعد ${hours} ساعة${mins > 0 ? ` و${mins} دقيقة` : ""}`;
                      }
                    })()}
                  </Box>
                )}

                {/* Table Info */}
                {table.status !== "متاح" && (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        position: "absolute",
                        top: isSpecificSize ? "68px" : "75px",
                        left: "8px",
                        color: mode === "dark" ? "#ccc" : "#666",
                        fontSize: fontSize.small,
                      }}
                    >
                      {`فتح: ${formatTime12(table.openTime || table.openDateTime)}`}
                    </Typography>

                    {table.status === "محجوز" && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: isSpecificSize ? 90 : 100,
                          left: 8,
                          zIndex: 10,
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon sx={{ fontSize: isSpecificSize ? 14 : 16 }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowReservations(table);
                          }}
                          sx={{
                            fontSize: fontSize.small,
                            padding: isSpecificSize ? "1px 4px" : "2px 6px",
                            minWidth: "unset",
                            borderRadius: isSpecificSize ? "4px" : "6px",
                            minHeight: 32,
                          }}
                        >
                          عرض
                        </Button>
                        {table.nearestReservation && (
                          <Box
                            sx={{
                              mt: 0.8,
                              fontWeight: "bold",
                              color: "#ff9800",
                              fontSize: fontSize.small,
                              background: "#fffbe6",
                              borderRadius: isSpecificSize ? "6px" : "8px",
                              px: isSpecificSize ? 1.5 : 2,
                              py: isSpecificSize ? 0.3 : 0.5,
                              boxShadow: "0 2px 8px #ff980088",
                              animation: "blinker 1.2s linear infinite",
                              display: "inline-block",
                            }}
                          >
                            {table.nearestReservation.diffMinutes > 0
                              ? table.nearestReservation.diffMinutes < 60
                                ? `الحجز بعد ${table.nearestReservation.diffMinutes} دقيقة`
                                : (() => {
                                    const hours = Math.floor(table.nearestReservation.diffMinutes / 60);
                                    const mins = table.nearestReservation.diffMinutes % 60;
                                    return `الحجز بعد ${hours} ساعة${mins > 0 ? ` و${mins} دقيقة` : ""}`;
                                  })()
                              : "وقت الحجز الآن"}
                          </Box>
                        )}
                      </Box>
                    )}

                    <Box
                      sx={{ 
                        position: "absolute", 
                        top: isSpecificSize ? "90px" : "100px", 
                        right: "8px" 
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: "bold", fontSize: fontSize.body }}
                      >
                        العميل: {table.customer}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "medium",
                          fontSize: fontSize.small,
                          color: "#aaa",
                        }}
                      >
                        الكابتن: {table.captain}
                      </Typography>
                    </Box>
                  </>
                )}

                {/* Zero Total Warning */}
                {table.total === 0 && table.status !== "متاح" && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: isSpecificSize ? 38 : 45,
                      left: isSpecificSize ? 15 : 20,
                      bgcolor: "#ff1744",
                      color: "#fff",
                      borderRadius: isSpecificSize ? "6px" : "8px",
                      px: isSpecificSize ? 1.5 : 2,
                      py: badgeSize.py,
                      fontWeight: "bold",
                      fontSize: fontSize.small,
                      zIndex: 5,
                      boxShadow: "0 2px 8px #ff174488",
                      animation: "blinker 1.2s linear infinite",
                    }}
                  >
                    ⚠️
                  </Box>
                )}

                {/* Total Badge */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    bgcolor: "#F8BBD0",
                    color: "#fff",
                    borderRadius: isSpecificSize ? "6px 0 6px 6px" : "8px 0 8px 8px",
                    px: isSpecificSize ? 1.5 : 2,
                    py: badgeSize.py,
                    fontWeight: "bold",
                    fontSize: fontSize.body,
                    minWidth: isSpecificSize ? "60px" : "70px",
                    textAlign: "center",
                  }}
                >
                  {table.status !== "متاح" ? ` ${table.total}` : null}
                </Box>

                {/* Confirm Button for External Orders */}
                {table.status === "اوردر خارجي" && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: isSpecificSize ? 10 : 12,
                      left: isSpecificSize ? 10 : 12,
                      zIndex: 10,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="success"
                      size={getButtonSize()}
                      sx={{
                        fontWeight: "bold",
                        borderRadius: isSpecificSize ? "8px" : "10px",
                        px: isSpecificSize ? 2 : 3,
                        py: isSpecificSize ? 0.8 : 1,
                        fontSize: fontSize.body,
                        boxShadow: "0 2px 8px #43a04744",
                        minHeight: 36,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleConfirmExternalOrder(table);
                      }}
                    >
                      تأكيد
                    </Button>
                  </Box>
                )}
              </Paper>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Success Snackbar */}
      <Snackbar
        open={showConfirmSuccess}
        autoHideDuration={3500}
        onClose={() => setShowConfirmSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          sx={{
            fontSize: fontSize.title,
            fontWeight: "bold",
            bgcolor: "#fffbe6",
            color: "#43a047",
            borderRadius: 2,
            boxShadow: "0 2px 12px #43a04722",
            textAlign: "center",
            px: 3,
            py: 2,
          }}
        >
          ✅ تم تأكيد الأوردر الخارجي بنجاح!
        </Alert>
      </Snackbar>

      {/* No Permission Snackbar */}
      <Snackbar
        open={showNoPermission}
        autoHideDuration={3000}
        onClose={() => setShowNoPermission(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => setShowNoPermission(false)}
          sx={{
            fontSize: fontSize.body,
            fontWeight: 700,
            bgcolor: "#ffebee",
            color: "#c62828",
            borderRadius: 1,
          }}
        >
          ليس لديك صلاحية لفتح الطاولة.
        </Alert>
      </Snackbar>

      {/* Reservations Dialog */}
      <Dialog
        open={reservationDialogOpen}
        onClose={() => setReservationDialogOpen(false)}
        maxWidth={isSmallTablet ? "xs" : "sm"}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: isSpecificSize ? 2 : 3,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: fontSize.title }}>
          حجوزات الطاولة{" "}
          {reservationDialogTable?.name ? `(${reservationDialogTable.name})` : ""}
        </DialogTitle>
        <DialogContent>
          {reservationDialogLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
              <CircularProgress size={isSpecificSize ? 32 : 40} />
            </Box>
          ) : reservationDialogData.length === 0 ? (
            <Typography 
              color="text.secondary" 
              align="center" 
              sx={{ my: 2, fontSize: fontSize.body }}
            >
              لا توجد حجوزات معلقة لهذه الطاولة.
            </Typography>
          ) : (
            <Box sx={{ mt: 1, overflowX: "auto" }}>
              <Table size={isSpecificSize ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: fontSize.small }}>التاريخ</TableCell>
                    <TableCell sx={{ fontSize: fontSize.small }}>الوقت</TableCell>
                    <TableCell sx={{ fontSize: fontSize.small }}>عدد الأشخاص</TableCell>
                    <TableCell sx={{ fontSize: fontSize.small }}>الحالة</TableCell>
                    {!isSpecificSize && <TableCell sx={{ fontSize: fontSize.small }}>ملاحظات</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reservationDialogData.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell sx={{ fontSize: fontSize.small }}>{r.reservation_date}</TableCell>
                      <TableCell sx={{ fontSize: fontSize.small }}>
                        {(() => {
                          const [h, m, s] = r.reservation_time.split(":");
                          let hour = Number(h);
                          const ampm = hour >= 12 ? "م" : "ص";
                          hour = hour % 12 || 12;
                          return `${hour}:${m} ${ampm}`;
                        })()}
                      </TableCell>
                      <TableCell sx={{ fontSize: fontSize.small }}>{r.number_of_people}</TableCell>
                      <TableCell sx={{ fontSize: fontSize.small }}>
                        {r.status === "pending" ? "معلق" : r.status}
                      </TableCell>
                      {!isSpecificSize && <TableCell sx={{ fontSize: fontSize.small }}>{r.note || "-"}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setReservationDialogOpen(false)}
            size={getButtonSize()}
          >
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sub Orders Dialog */}
      <Dialog
        open={selectSubOrderModal}
        onClose={() => setSelectSubOrderModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: isSpecificSize ? 2 : 3,
            bgcolor: mode === "dark" ? "#232334" : "#fff",
            color: mode === "dark" ? "#fff" : "#232334",
          },
        }}
      >
        <DialogTitle
          sx={{ 
            fontWeight: 700, 
            fontSize: fontSize.title, 
            textAlign: "center" 
          }}
        >
          اختر الأوردر المطلوب للطاولة
        </DialogTitle>
        <DialogContent>
          <List>
            {subOrders.map((order) => (
              <ListItem key={order.id} disablePadding>
                <ListItemButton
                  onClick={() => handleSelectSubOrder(order)}
                  sx={{
                    borderRadius: isSpecificSize ? 1.5 : 2,
                    mb: 1,
                    bgcolor: order.isMain ? "#fbc2eb" : undefined,
                    color: order.isMain ? "#232334" : undefined,
                    fontWeight: order.isMain ? 700 : 500,
                    border: order.isMain ? "2px solid #fbc2eb" : "1px solid #eee",
                    minHeight: 44,
                  }}
                >
                  <ListItemText
                    primary={order.sub_table_name}
                    secondary={order.isMain ? "الأوردر الأساسي" : undefined}
                    primaryTypographyProps={{
                      fontWeight: order.isMain ? 700 : 500,
                      fontSize: fontSize.body,
                    }}
                    secondaryTypographyProps={{
                      fontSize: fontSize.small,
                      color: "#888",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSelectSubOrderModal(false)}
            color="secondary"
            size={getButtonSize()}
          >
            إلغاء
          </Button>
        </DialogActions>
      </Dialog>

      {/* NEW: Dialog عرض أصناف الأوردر */}
      <OrderItemsDialog
        open={orderItemsDialogOpen}
        onClose={handleCloseOrderItems}
        orderId={selectedOrderId}
      />

      {/* Main Modal */}
      <Modal
        open={isModalOpen}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: isSpecificSize ? 0.5 : { xs: 1, sm: 2 },
        }}
      >
        <Fade in={isModalOpen}>
          <Box
            sx={{
              bgcolor: mode === "dark" ? "#1F1D2B" : "#fff",
              padding: isSpecificSize ? 1 : { xs: 1, sm: 2, md: 3 },
              borderRadius: isSpecificSize ? "12px" : "16px",
              boxShadow: "0px 8px 24px rgba(0,0,0,0.35)",
              maxHeight: modalType === "createOrder"
                ? isSpecificSize 
                  ? "96vh" 
                  : { xs: "98vh", sm: "90vh", md: "80vh" }
                : "95vh",
              height: modalType === "createOrder" ? "auto" : "95vh",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "#444 #1a1a1a",
              "&::-webkit-scrollbar": {
                width: isSpecificSize ? "4px" : "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#444",
                borderRadius: "10px",
              },
              width: modalType === "createOrder"
                ? isSpecificSize
                  ? "98vw"
                  : { xs: "99vw", sm: "90vw", md: "700px" }
                : isSpecificSize
                ? "98vw"
                : { xs: "99vw", sm: "95vw", md: "1200px", lg: "1600px" },
              position: "relative",
              maxWidth: "100vw",
            }}
          >
            <IconButton
              onClick={() => setIsModalOpen(false)}
              sx={{ 
                position: "absolute", 
                top: isSpecificSize ? 4 : 8, 
                right: isSpecificSize ? 4 : 8, 
                zIndex: 10,
                minHeight: 40,
                minWidth: 40,
              }}
            >
              <CloseIcon sx={{ fontSize: fontSize.title }} />
            </IconButton>

            {modalType === "createOrder" && (
              <CreateOrderForm
                tableId={selectedTable?.id}
                rootId={rootId}
                hasReservation={selectedTable?.reservation === 1}
                onSuccess={(newOrder) => {
                  setIsModalOpen(false);

                  // 🔄 تحديث الطاولة الحالية فقط بدون fetchAllTables
                  setTables((prev) =>
                    prev.map((t) =>
                      t.id === selectedTable?.id
                        ? {
                            ...t,
                            orderId: newOrder.id,
                            status: "مفتوح",
                            customer: newOrder.customer || "",
                            captain: newOrder.captain_name || "",
                            total: newOrder.total || 0,
                            openDateTime: new Date().toLocaleString(),
                            // الحقول الجديدة: استخدم القيم من newOrder إن وجدت
                            openTime:
                              newOrder.open_time ||
                              newOrder.openTime ||
                              (new Date().toLocaleTimeString() || ""),
                            orderStatus: newOrder.status || "",
                          }
                        : t
                    )
                  );

                  setFilteredTables((prev) =>
                    prev.map((t) =>
                      t.id === selectedTable?.id
                        ? {
                            ...t,
                            orderId: newOrder.id,
                            status: "مفتوح",
                            customer: newOrder.customer || "",
                            captain: newOrder.captain_name || "",
                            total: newOrder.total || 0,
                            openDateTime: new Date().toLocaleString(),
                            openTime:
                              newOrder.open_time ||
                              newOrder.openTime ||
                              (new Date().toLocaleTimeString() || ""),
                            orderStatus: newOrder.status || "",
                          }
                        : t
                    )
                  );

                  setSelectedTable((prev) =>
                    prev
                      ? {
                          ...prev,
                          orderId: newOrder.id,
                          status: "مفتوح",
                          customer: newOrder.customer || "",
                          captain: newOrder.captain_name || "",
                          total: newOrder.total || 0,
                          openDateTime: new Date().toLocaleString(),
                          openTime:
                            newOrder.open_time ||
                            newOrder.openTime ||
                            (new Date().toLocaleTimeString() || ""),
                          orderStatus: newOrder.status || "",
                        }
                      : prev
                  );

                  setOrderId(newOrder.id);
                  setModalType("addItem");
                  setIsModalOpen(true);
                }}
              />
            )}

            {modalType === "addItem" && (
              <TableModalContent
                type="addItem"
                tableId={selectedTable?.id}
                rootId={rootId}
                orderId={orderId}
                onSuccess={refreshAndReopen}
                onClose={() => setIsModalOpen(false)}
                onOrderDeleted={handleOrderDeleteSuccess}
              />
            )}
          </Box>
        </Fade>
      </Modal>

      <style>
        {`
          @keyframes blinker {
            50% { opacity: 0.4; }
          }
          @keyframes pulse {
            0% {
              transform: scale(1);
              box-shadow: 0 6px 18px rgba(255,107,107,0.12);
            }
            50% {
              transform: scale(1.03);
              box-shadow: 0 10px 30px rgba(255,107,107,0.28);
            }
            100% {
              transform: scale(1);
              box-shadow: 0 6px 18px rgba(255,107,107,0.12);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default TabletTablesSection;